import { useRef } from "react";
import { cn } from "@/lib/utils";

// The six places the server knows (settings_routes.ISO_POSITIONS), in reading order.
export const ISO_POSITIONS = ["top-left", "top-center", "top-right", "bottom-left", "bottom-center", "bottom-right"];
const WORDS = {
  "top-left": "top left", "top-center": "top centre", "top-right": "top right",
  "bottom-left": "bottom left", "bottom-center": "bottom centre", "bottom-right": "bottom right",
};
export const positionWords = (position) => WORDS[position] || WORDS["bottom-center"];
export function splitPosition(position) {
  const [vertical, align] = (ISO_POSITIONS.includes(position) ? position : "bottom-center").split("-");
  return { vertical, align };
}

/**
 * Where the ISO line prints, picked on a sketch of the page itself: three places along the top
 * edge and three along the bottom. A radio group — Tab lands on the chosen place and the arrow keys
 * move it — and the choice is repeated in words beneath, because a highlighted corner on its own is
 * easy to misread.
 */
export default function IsoPlacementPicker({ label, value, onChange, shape, disabled, testid }) {
  const buttons = useRef({});
  const current = ISO_POSITIONS.includes(value) ? value : "bottom-center";
  const landscape = shape === "landscape";

  const choose = (position) => {
    onChange(position);
    buttons.current[position]?.focus();
  };
  const onKeyDown = (e) => {
    const i = ISO_POSITIONS.indexOf(current);
    const row = Math.floor(i / 3);
    const col = i % 3;
    const to = {
      ArrowLeft: [row, (col + 2) % 3], ArrowRight: [row, (col + 1) % 3], ArrowUp: [0, col], ArrowDown: [1, col],
    }[e.key];
    if (!to || disabled) return;
    e.preventDefault();
    choose(ISO_POSITIONS[to[0] * 3 + to[1]]);
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div id={`${testid}-label`} className="text-sm font-medium">{label}</div>
      <div role="radiogroup" aria-labelledby={`${testid}-label`} aria-disabled={disabled || undefined}
           onKeyDown={onKeyDown}
           className={cn("relative rounded-md border border-border bg-card shadow-sm",
             landscape ? "w-[224px] aspect-[297/210]" : "w-[112px] aspect-[210/297]",
             disabled && "opacity-60")}>
        {/* The page's own content, sketched, so the six places read as the edges of a page. */}
        <div aria-hidden="true"
             className={cn("absolute flex flex-col items-center justify-center gap-1.5",
               landscape ? "inset-x-[20%] inset-y-[27%]" : "inset-x-[14%] inset-y-[23%]")}>
          {landscape ? (
            <>
              <span className="h-1.5 w-1/3 rounded-full bg-muted-foreground/25" />
              <span className="h-2 w-4/5 rounded-full bg-muted-foreground/35" />
              <span className="h-1.5 w-1/2 rounded-full bg-primary/30" />
              <span className="h-1 w-3/4 rounded-full bg-muted-foreground/20" />
            </>
          ) : (
            <>
              <span className="h-1.5 w-2/3 rounded-full bg-muted-foreground/35" />
              {[0, 1, 2, 3, 4].map((k) => <span key={k} className="h-1 w-full rounded-full bg-muted-foreground/20" />)}
            </>
          )}
        </div>
        {["top", "bottom"].map((vertical) => (
          <div key={vertical}
               className={cn("absolute inset-x-1.5 grid grid-cols-3 gap-1", vertical === "top" ? "top-1.5" : "bottom-1.5")}>
            {["left", "center", "right"].map((align) => {
              const position = `${vertical}-${align}`;
              const on = position === current;
              return (
                <button key={position} type="button" role="radio" aria-checked={on}
                        aria-label={positionWords(position)}
                        ref={(el) => { buttons.current[position] = el; }}
                        tabIndex={on ? 0 : -1} disabled={disabled} onClick={() => choose(position)}
                        data-testid={`${testid}-${position}`}
                        className={cn(
                          "flex h-6 items-center rounded-sm border px-1 transition-colors",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card",
                          align === "left" ? "justify-start" : align === "right" ? "justify-end" : "justify-center",
                          on ? "border-primary bg-primary/10" : "border-dashed border-border hover:border-primary/60 hover:bg-muted",
                          disabled && "cursor-not-allowed hover:border-border hover:bg-transparent")}>
                  <span className={cn("h-1.5 rounded-full", on ? "w-4/5 bg-primary" : "w-2/5 bg-muted-foreground/30")} />
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground" data-testid={`${testid}-words`}>
        Prints <span className="font-medium text-foreground">{positionWords(current)}</span>
      </p>
    </div>
  );
}
